import { BookOutlined, CompassOutlined, DeploymentUnitOutlined, FontSizeOutlined, ReadOutlined, SafetyOutlined } from "@ant-design/icons";
import { type Language } from "@ap/i18n";
import { Term } from "@ap/ui-kit";
import { Drawer } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { routeOf } from "../routes";

/**
 * Help drawer (ADR-0055): what the current page is for (help:pages.<routeKey>), its key terms, and
 * links to the matching user-guide chapter (bundled, /guide), How it works, Glossary and the tour.
 */
export function HelpDrawer({ open, onClose, lang, onTour }: { open: boolean; onClose: () => void; lang: Language; onTour: () => void }) {
  const { t } = useTranslation("help");
  const { t: ts } = useTranslation("shell");
  const loc = useLocation();
  const route = routeOf(loc.pathname);
  const key = route?.key ?? "home";
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("title")}
      placement={lang === "ar" ? "left" : "right"}
      width={400}
      className="ap-help"
      data-testid="help-drawer"
    >
      <section aria-labelledby="help-about" className="ap-help-section">
        <h2 id="help-about" className="ap-help-h">
          {t("aboutPage")}: {route ? ts(route.label) : ts("product")}
        </h2>
        <p data-testid="help-body">{t(`pages.${key}`)}</p>
        {route?.terms?.length ? (
          <p className="ap-help-terms">
            {route.terms.map((id) => (
              <Term key={id} id={id} />
            ))}
          </p>
        ) : null}
      </section>
      <ul className="ap-help-links">
        {route ? (
          <li>
            <Link to={`/guide/${route.chapter}`} onClick={onClose}>
              <ReadOutlined aria-hidden /> {t("guide")}
            </Link>
          </li>
        ) : null}
        <li>
          <Link to="/how-it-works" onClick={onClose}>
            <DeploymentUnitOutlined aria-hidden /> {t("howItWorks")}
          </Link>
        </li>
        <li>
          <Link to="/data-levels" onClick={onClose}>
            <SafetyOutlined aria-hidden /> {ts("nav.dataLevels")}
          </Link>
        </li>
        <li>
          <Link to="/glossary" onClick={onClose}>
            <FontSizeOutlined aria-hidden /> {t("glossary")}
          </Link>
        </li>
        <li>
          <Link to="/guide" onClick={onClose}>
            <BookOutlined aria-hidden /> {t("guideAll")}
          </Link>
        </li>
        <li>
          <button
            type="button"
            className="ap-linklike"
            onClick={() => {
              onClose();
              onTour();
            }}
            data-testid="help-tour"
          >
            <CompassOutlined aria-hidden /> {t("tour")}
          </button>
        </li>
      </ul>
      <p className="ap-muted ap-help-tip">{t("shortcuts")}</p>
    </Drawer>
  );
}
